export type ApiResponse<T> =
  | { success: true; data: T }
  | { success: false; error: string };

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  let payload: ApiResponse<T> | null = null;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    // empty body
  }

  if (!res.ok || !payload || payload.success === false) {
    const message =
      payload && payload.success === false ? payload.error : `Request failed (${res.status})`;
    throw new ApiError(res.status, message);
  }
  return payload.data;
}

export type Paginated<T> = {
  items: T[];
  pagination: { page: number; limit: number; total: number; pages: number };
};

export type ProductDTO = {
  id: number;
  name: string;
  description: string | null;
  priceCents: number;
  stock: number;
  category: string | null;
  imageUrl: string | null;
  createdAt: string;
};

export type OrderItemDTO = {
  id: number;
  orderId: number;
  productId: number;
  quantity: number;
  unitPriceCents: number;
};

export type OrderDTO = {
  id: number;
  orderNo: string;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
  totalAmountCents: number;
  status: 'pending' | 'paid' | 'shipped' | 'delivered' | 'cancelled';
  shippingAddress: string | null;
  notes: string | null;
  createdAt: string;
  items?: OrderItemDTO[];
};

type ProductInput = {
  name: string;
  description?: string | null;
  priceCents: number;
  stock?: number;
  category?: string | null;
  imageUrl?: string | null;
};

function buildQs(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  return qs.toString() ? `?${qs.toString()}` : '';
}

export const api = {
  products: {
    list: (params: { page?: number; limit?: number; category?: string; search?: string } = {}) =>
      request<Paginated<ProductDTO>>(`/products${buildQs(params)}`),
    get: (id: number) => request<ProductDTO>(`/products/${id}`),
    create: (body: ProductInput) =>
      request<ProductDTO>('/products', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<ProductInput>) =>
      request<ProductDTO>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    delete: (id: number) =>
      request<{ id: number }>(`/products/${id}`, { method: 'DELETE' }),
  },
  orders: {
    list: (params: { page?: number; limit?: number; status?: string } = {}) =>
      request<Paginated<OrderDTO>>(`/orders${buildQs(params)}`),
    get: (id: number) => request<OrderDTO>(`/orders/${id}`),
    create: (body: {
      customerName: string;
      customerEmail?: string | null;
      customerPhone?: string | null;
      shippingAddress?: string | null;
      notes?: string | null;
      items: { productId: number; quantity: number }[];
    }) =>
      request<OrderDTO>('/orders', { method: 'POST', body: JSON.stringify(body) }),
    updateStatus: (id: number, status: OrderDTO['status']) =>
      request<OrderDTO>(`/orders/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
  },
  auth: {
    login: (email: string, password: string) =>
      request<{ id: number; email: string; name: string | null }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    logout: () => request<null>('/auth/logout', { method: 'POST' }),
    me: () =>
      request<{ id: number; email: string; name: string | null }>('/auth/me'),
  },
};
